import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon } from 'lucide-react';
import { warn as logWarn } from '../../utils/logger';
interface ContextSpotlightProps {
  /** The passage text. The target word/phrase will be located inside this. */
  passage: string;
  /** The exact word or phrase to spotlight. Case-sensitive substring match. */
  targetWord: string;
  /** Optional glossary tooltip / hint shown next to the spotlight. */
  gloss?: string;
  /** Optional source attribution. */
  source?: string;
  /** Optional label rendered above the passage. */
  label?: string;
  /** 'stimulus' = visible while answering, 'support' = post-answer reveal. */
  mode?: 'stimulus' | 'support';
}
/**
 * ContextSpotlight — Vocabulary-in-context visual for woordenschat families.
 *
 * Shows a reading passage with one word/phrase highlighted in a yellow
 * spotlight. Used by TAA-WOORD-CONTEXT, WOORD-IDIOOM and related families
 * where the learner must infer meaning from surrounding context.
 *
 * Differs from readingPassage:
 *   - readingPassage = shows entire passage neutrally for tekstbegrip Qs.
 *   - contextSpotlight = focuses attention on ONE target token; other text
 *     is visually de-emphasised so the learner knows where to look.
 *
 * Seed data:
 * ```json
 * {
 *   "type": "contextSpotlight",
 *   "data": {
 *     "passage": "De jongen was zo blij dat hij in de wolken was.",
 *     "targetWord": "in de wolken",
 *     "gloss": "uitdrukking"
 *   }
 * }
 * ```
 */
export function ContextSpotlight({
  passage,
  targetWord,
  gloss,
  source,
  label,
  mode = 'stimulus'
}: ContextSpotlightProps) {
  const isStimulus = mode === 'stimulus';
  // Split passage by targetWord (case-insensitive) to highlight all occurrences
  const regex = new RegExp(`(${targetWord})`, 'gi');
  const parts = passage.split(regex);
  if (parts.length === 1) {
    logWarn(`[ContextSpotlight] targetWord "${targetWord}" not found in passage. Rendering plain text.`);
  }
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="w-full" data-id="element-12010">
      {label && <div className="flex items-center gap-2 mb-3" data-id="element-12011">
          <ZapIcon className="w-4 h-4 text-amber-600" data-id="element-12012" />
          <span className="text-sm font-semibold text-amber-800 uppercase tracking-wide" data-id="element-12013">
            {label}
          </span>
        </div>}

      <div className={`relative bg-slate-50 border border-slate-200 rounded-xl ${isStimulus ? 'p-4 sm:p-5' : 'p-5 sm:p-6'}`} data-id="element-12014">
        <div className={`text-slate-500 leading-relaxed whitespace-pre-line ${isStimulus ? 'text-base' : 'text-lg'}`} data-id="element-12015">
          {parts.map((part, i) => {
          if (part.toLowerCase() === targetWord.toLowerCase()) {
            return <motion.span key={i} initial={{
              backgroundColor: 'rgba(253, 230, 138, 0.0)'
            }} animate={{
              backgroundColor: 'rgba(253, 230, 138, 1)'
            }} transition={{
              delay: 0.25,
              duration: 0.4
            }} className="inline rounded px-1.5 py-0.5 mx-0.5 font-bold text-slate-900 shadow-sm border border-amber-300" data-id="element-12016">
                  {part}
                </motion.span>;
          }
          return <span key={i} data-id="element-12017">{part}</span>;
        })}
        </div>

        {gloss && <div className="mt-3 pt-2 border-t border-slate-200 flex items-center gap-2" data-id="element-12018">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold" data-id="element-12019">
              i
            </span>
            <span className="text-xs text-slate-600 italic" data-id="element-12020">{gloss}</span>
          </div>}

        {source && <div className="mt-2 text-xs text-slate-400 italic" data-id="element-12021">{source}</div>}
      </div>
    </motion.div>;
}